import React, { useCallback, useContext, useEffect, useState } from 'react';
import { ClusterOutlined, EllipsisOutlined, ExclamationCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Table, Typography } from 'antd';
import { apiClient, apiErrorMessage, hasPermission } from '../../../utils';
import { CustomerClass } from '../../../models';
import { UserContext } from '../../contexts';
import { CustomerClassEditPage } from './customer-class-edit-page';
import { EntityChangeLog } from '../../reports';

export function CustomerClassesPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const [customerClasses, setCustomerClasses] = useState<CustomerClass[]>();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalCustomerClass, seteditModalCustomerClass] = useState<CustomerClass>();
  const [canEdit, setCanEdit] = useState(false);
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<string>();

  useEffect(() => {
    setCanEdit(hasPermission(user, 'customerClasses.edit'));
  }, [user]);

  const loadCustomerClasses = useCallback(() => {
    setLoading(true);
    apiClient
      .get(`customer-classes`)
      .then((response: any) => {
        setCustomerClasses(response.data);
        setLoading(false);
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadCustomerClasses();
  }, [loadCustomerClasses]);

  const onRefreshClick = () => {
    loadCustomerClasses();
  };

  const onCustomerClassEditSaved = () => {
    closeEditModal();
    loadCustomerClasses();
  };

  const openEditModal = () => {
    seteditModalCustomerClass(undefined);
    setEditModalOpen(true);
  };

  const openEditModalByCustomerClass = (customerClass: CustomerClass) => {
    seteditModalCustomerClass(customerClass);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    seteditModalCustomerClass(undefined);
  };

  const deleteCustomerClass = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`customer-classes/${id}`)
      .then(() => {
        setLoading(false);
        loadCustomerClasses();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const confirmdeleteCustomerClass = (id: string) => {
    const onOk = () => deleteCustomerClass(id);
    Modal.confirm({
      title: 'Müşteri sınıfı sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Müşteri sınıfı silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const openChangeLogModal = (id: string) => {
    setChangeLogModalOpen(id);
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'changelog') {
      openChangeLogModal(id);
    }
    if (e.key === 'edit') {
      const customerClass = customerClasses?.find(m => m.id === id);
      if (customerClass) {
        openEditModalByCustomerClass(customerClass);
      } else {
        openEditModal();
      }
    } else if (e.key === 'del') {
      confirmdeleteCustomerClass(id);
    }
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const tableActions = (id: string) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
      {canEdit && (
        <>
          <Menu.Item key="edit">
            <Text>Düzenle</Text>
          </Menu.Item>
          <Menu.Item key="del">
            <Text type="danger">Sil</Text>
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  const tableColumns: any[] = [
    {
      title: 'İsim',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, customerClass: CustomerClass) => (
        <Button type="link" onClick={() => openEditModalByCustomerClass(customerClass)}>
          {name}
        </Button>
      ),
    },
    {
      title: 'Öncelik puanı',
      dataIndex: 'priorityScore',
      key: 'priorityScore',
    },
    {
      title: '',
      key: 'action',
      render: (_: any, customerClass: CustomerClass) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(customerClass.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <ClusterOutlined /> Müşteri sınıfları
          </>
        }
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <>
            {canEdit && (
              <Button key="add" onClick={openEditModal} icon={<PlusOutlined />} type="primary">
                Yeni müşteri sınıfı
              </Button>
            )}
          </>,
        ]}
      />
      <Content>
        <Table columns={tableColumns} dataSource={customerClasses} loading={loading} pagination={false} />
      </Content>
      <Modal
        title="Değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={'CustomerClass'} recordId={changeLogModalOpen} />
      </Modal>
      <Modal title="Müşteri sınıfı" visible={editModalOpen} footer={null} onCancel={closeEditModal} destroyOnClose={true}>
        <CustomerClassEditPage customerClass={editModalCustomerClass} onCancel={closeEditModal} onSaved={onCustomerClassEditSaved} />
      </Modal>
    </>
  );
}
